import React, { createContext, useContext, ReactNode, useState } from "react";
import { Dayjs } from "dayjs";
import dayjs from "@dayjs";
import { DATE_FORMAT, SEARCH_PARAMS } from "@/app/utils/constants/constants";
import { getLastSearchRequestFromLocalStorage } from "@/app/utils/services/service/localStorageService";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";

type DatePickerContextType = {
  selectedDate: [Dayjs | null, Dayjs | null];
  setSelectedDate: React.Dispatch<
    React.SetStateAction<[Dayjs | null, Dayjs | null]>
  >;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  handleDateChange: (dates: [Dayjs | null, Dayjs | null]) => void;
};

const DatePickerContext = createContext<DatePickerContextType | undefined>(
  undefined
);

export const useDatePickerContext = () => {
  const context = useContext(DatePickerContext);
  if (!context) {
    throw new Error("useDatePickerContext error");
  }
  return context;
};

type Props = {
  children: ReactNode;
};

export const DatePickerProvider = ({ children }: Props) => {
  const searchParams = useSearchParams();
  const locale = useLocale();
  const lastSearchRequest = getLastSearchRequestFromLocalStorage(locale);

  // Get dates from URL params first
  const urlFrom = searchParams.get(SEARCH_PARAMS.CHECKIN);
  const urlTo = searchParams.get(SEARCH_PARAMS.CHECKOUT);

  const { from, to } = lastSearchRequest || {};
  const today = dayjs();
  const isFromInPast =
    (from && dayjs(from).isBefore(today, "day")) ||
    (urlFrom && dayjs(urlFrom).isBefore(today, "day"));

  const [selectedDate, setSelectedDate] = useState<
    [Dayjs | null, Dayjs | null]
  >(() => {
    // Priority 1: URL params
    if (urlFrom && urlTo && !isFromInPast) {
      return [dayjs(urlFrom), dayjs(urlTo)];
    }

    // Priority 2: localStorage (if dates are valid)
    if (lastSearchRequest && !isFromInPast) {
      return [dayjs(from), dayjs(to)];
    }

    // Default: tomorrow + 2 days
    return [
      dayjs(today.add(1, "day"), DATE_FORMAT),
      dayjs(today.add(2, "day"), DATE_FORMAT),
    ];
  });

  const [error, setError] = useState<boolean>(false);

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null]) => {
    setError(false);
    setSelectedDate(dates);
  };

  return (
    <DatePickerContext.Provider
      value={{
        selectedDate,
        setSelectedDate,
        error,
        setError,
        handleDateChange,
      }}
    >
      {children}
    </DatePickerContext.Provider>
  );
};
